import React, { useEffect, useRef, useState } from 'react';
import './slider.scss';

function getClientPosition(e) {
  const touches = e.touches;

  if (touches && touches.length) {
    const finger = touches[0];
    return {
      x: finger.clientX,
      y: finger.clientY,
    };
  }

  return {
    x: e.clientX,
    y: e.clientY,
  };
}

type SliderProps = {
  disabled: boolean;
  x: number;
  xMin?: number;
  xMax: number;
  xLabelFormatter: (x: number) => string;
  onChange: ({ x }: { x: number }) => void;
  automationIdPrefix?: string;
  singleOption?: (x?: number) => string;
  onDragStart?: (
    event: React.TouchEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>,
  ) => void;
  onDragEnd?: (event: TouchEvent | MouseEvent) => void;
  onClick?: (event: React.MouseEvent) => void;
  isMobile?: boolean;
};

export default function Slider({
  disabled,
  x,
  xMin = 0,
  xMax,
  xLabelFormatter = (x) => {
    return `R${x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}`;
  },
  onChange,
  onDragStart,
  onDragEnd,
  onClick,
  automationIdPrefix,
  isMobile = false,
  singleOption = () => '',
}: SliderProps) {
  const [valueSnapOffset, setValueSnapOffset] = useState<number>(82);
  const container = useRef<HTMLDivElement>(null);
  const handle = useRef<HTMLDivElement>(null);
  let startX = 0;
  let offsetX = 0;

  var oldVals = { x: 0, xNew: 0 };

  const sameAsPreviousValues = (x, xNew) => oldVals.x === x && oldVals.xNew === xNew;
  const updateOldValues = (x, xNew) => (oldVals = { x, xNew });

  useEffect(() => {
    if (isMobile) {
      setValueSnapOffset(55);
    } else {
      setValueSnapOffset(82);
    }
  }, [isMobile]);

  function getPosition() {
    let left = ((x - xMin) / (xMax - xMin)) * 100;

    if (left > 100) left = 100;
    if (left < 0) left = 0;

    return { left };
  }

  function change({ left }) {
    if (!onChange) return;

    if (!container.current) return;
    const { width } = container.current.getBoundingClientRect();

    if (left < 0) left = 0;
    if (left > width) left = width;

    const dx = (left / width) * (xMax - xMin);

    const xNew = dx !== 0 ? Math.round(dx) : 0;

    if (x !== xNew && !sameAsPreviousValues(x, xNew)) {
      updateOldValues(x, xNew);
      onChange({
        x: xNew,
      });
    }
  }

  function handleMouseDown(e: React.TouchEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>) {
    if (disabled) return;

    e.preventDefault();
    if (!handle.current) return;
    const dom = handle.current;
    const clientPos = getClientPosition(e);

    startX = dom.offsetLeft;
    offsetX = clientPos.x;

    document.addEventListener('mousemove', handleDrag);
    document.addEventListener('mouseup', handleDragEnd);
    document.addEventListener('touchmove', handleDrag, { passive: false });
    document.addEventListener('touchend', handleDragEnd);
    document.addEventListener('touchcancel', handleDragEnd);

    if (onDragStart) {
      onDragStart(e);
    }
  }

  function getPos(e: MouseEvent | TouchEvent) {
    const clientPos = getClientPosition(e);
    const left = clientPos.x + startX - offsetX;

    return { left };
  }

  function handleDrag(e: MouseEvent | TouchEvent) {
    if (disabled) return;
    e.preventDefault();
    change(getPos(e));
  }

  function handleDragEnd(e: MouseEvent | TouchEvent) {
    if (disabled) return;

    e.preventDefault();
    document.removeEventListener('mousemove', handleDrag);
    document.removeEventListener('mouseup', handleDragEnd);

    document.removeEventListener('touchmove', handleDrag);
    document.removeEventListener('touchend', handleDragEnd);
    document.removeEventListener('touchcancel', handleDragEnd);

    if (onDragEnd) {
      onDragEnd(e);
    }
  }

  function handleClick(e: React.MouseEvent) {
    if (disabled) return;

    const clientPos = getClientPosition(e);
    if (!container.current) return;
    const rect = container.current.getBoundingClientRect();

    change({
      left: clientPos.x - rect.left,
    });

    if (onClick) onClick(e);
  }

  const pos = getPosition();
  const valueStyle = { width: disabled ? '100%' : pos.left + '%' };

  const handleStyle = {
    position: 'absolute',
    transform: 'translate(-50%, -50%)',
    left: disabled ? '100%' : pos.left + '%',
    top: '50%',
  };

  const labelText = xLabelFormatter(x) || singleOption(x);

  const labelStyle = (function (isDisabled, posLeft, labelTextLength) {
    if (posLeft > valueSnapOffset || isDisabled) return { float: 'right' };
    else if (posLeft < 15) return { position: 'relative' };
    else
      return {
        left: `${posLeft}%`,
        marginLeft: `-${labelTextLength * 10.5}px`,
      };
  })(disabled, pos.left, labelText.length);

  return (
    <div className="Slider" automationid={`${automationIdPrefix}_Div_Container`}>
      <div ref={container} className="slider-track" onClick={handleClick}>
        <div className="slider-active" style={valueStyle} />
        <div
          ref={handle}
          // @ts-ignore
          style={handleStyle}
          onTouchStart={handleMouseDown}
          onMouseDown={handleMouseDown}
          onClick={function (e) {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
          automationid={`${automationIdPrefix}_Div_Slider_Bar`}
        >
          <div
            className="slider-thumb"
            automationid={`${automationIdPrefix}_Div_Slider_Actual_Amount_Disc_Control`}
          />
        </div>
      </div>
      <div className="slider-label-container">
        <span
          className="slider-min-label"
          style={{ display: pos.left <= 15 ? 'none' : '' }}
          automationid={`${automationIdPrefix}_Span_Slider_Min_Label`}
        >
          {xLabelFormatter(xMin)}
        </span>
        <span
          className="slider-label"
          // @ts-ignore
          style={labelStyle}
          automationid={`${automationIdPrefix}_Span_Slider_Actual_Amount_Label`}
        >
          {labelText}
        </span>
        <span
          className="slider-max-label"
          style={{ display: pos.left >= valueSnapOffset ? 'none' : '' }}
          automationid={`${automationIdPrefix}_Span_Slider_Max_Label`}
        >
          {xLabelFormatter(xMax)}
        </span>
      </div>
    </div>
  );
}
